import { memo } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { Device } from "types";

interface HeatmapCombinedSensorListProps {
    onSelectSensor?: (params: any) => void;
    onChange?: (updatedSensorList: any[]) => void;
    sensors?: Device[];
}

type Sensor = {
    key: string;
    sensorName?: string;
    sensorType?: string;
    gatewayName?: string;
    gatewayId?: string;
    deviceId?: string;
};

/**
 * Renders a heatmap combined sensor list and manages sensor selection and removal.
 *
 * @param {HeatmapCombinedSensorListProps} props - The component props containing sensor data and handlers.
 * @returns {JSX.Element} The rendered component.
 * @throws {Error} Throws an error if the sensor selection fails.
 */
const HeatmapCombinedSensorList = ({
    onSelectSensor,
    onChange,
    sensors,
}: HeatmapCombinedSensorListProps) => {
    const sensorList: Sensor[] = Array.from({ length: 4 }, (_, index) => ({
        key: `Sensor ${index + 1}`,
        sensorName: "",
        sensorType: "",
        gatewayName: "",
        gatewayId: "",
        deviceId: "",
        ...sensors?.[index],
    }));

    const handleChangeSensorList = (updatedSensorList: any) => {
        onChange?.(updatedSensorList);
    };

    const handleSelectSensor = (selectedSensors: any, currentKey: string) => {
        const selectedSensor = Object.values(selectedSensors)[0] as any;
        if (!selectedSensor) {
            return;
        }

        const index = sensorList.findIndex((item) => item.key === currentKey);
        const updatedSensorList = [...sensorList];
        updatedSensorList[index] = {
            ...updatedSensorList[index],
            ...selectedSensor,
        };

        handleChangeSensorList(updatedSensorList);
    };

    const removeSensor = (sensor: any) => {
        const index = sensorList.findIndex((item) => item.key === sensor.key);
        const updatedSensorList = [...sensorList];
        updatedSensorList[index] = {
            key: sensor.key,
        };

        handleChangeSensorList(updatedSensorList);
    };

    return (
        <Row className="cstm-table heatmap-sensor-table">
            <Col sm={12}>
                <div className="table-head">
                    <Row className="no-checkbox">
                        <Col xs={{ span: 2 }}></Col>
                        <Col xs={{ span: 2 }}>Sensor Name</Col>
                        <Col xs={{ span: 2 }}>Type</Col>
                        <Col xs={{ span: 2 }}>Gateway</Col>
                        <Col xs={{ span: 2 }}>UUID</Col>
                        <Col xs={{ span: 1 }}> </Col>
                    </Row>
                </div>
                {sensorList.map((sensor) => (
                    <div className="table-row" key={sensor.key}>
                        <Row className="no-checkbox flex-column flex-lg-row align-items-start align-items-lg-center">
                            <Col xs={{ span: "auto" }} lg={2} className="mb-1">
                                {sensor.key}
                            </Col>
                            <Col xs={{ span: "auto" }} lg={2}>
                                <span className="row-label">Sensor Name: </span>
                                {sensor.sensorName}
                            </Col>
                            <Col xs={{ span: "auto" }} lg={2}>
                                <span className="row-label">Type: </span>
                                {sensor.sensorType}
                            </Col>
                            <Col xs={{ span: "auto" }} lg={2}>
                                <span className="row-label">Gateway: </span>
                                {sensor.gatewayName}
                            </Col>
                            <Col xs={{ span: "auto" }} lg={2}>
                                <span className="row-label">UUID: </span>
                                {sensor.gatewayId}
                            </Col>
                            <Col
                                xs={{ span: 12 }}
                                lg={2}
                                className="text-right"
                            >
                                {sensor.gatewayId ? (
                                    <Button
                                        variant="link mr-0"
                                        className="p-0 text-decoration-none"
                                        onClick={() => removeSensor(sensor)}
                                    >
                                        <i className="material-icons close-icon d-flex">
                                            close
                                        </i>
                                    </Button>
                                ) : (
                                    <Button
                                        variant="primary mr-0"
                                        onClick={() => {
                                            if (
                                                typeof onSelectSensor ===
                                                "function"
                                            ) {
                                                onSelectSensor({
                                                    show: true,
                                                    excludeDevices: (
                                                        sensorList || []
                                                    )
                                                        .map(
                                                            (item) =>
                                                                item.deviceId
                                                        )
                                                        .filter(Boolean),
                                                    multipleSelection: false,
                                                    handler: (data: any) =>
                                                        handleSelectSensor(
                                                            data,
                                                            sensor.key
                                                        ),
                                                });
                                            }
                                        }}
                                    >
                                        Select Sensor
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </div>
                ))}
            </Col>
        </Row>
    );
};

export default memo(HeatmapCombinedSensorList);
